import { HttpClient, HttpEventType, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, filter, map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  FeedbackAssigneeOptionsResponse,
  Feedback,
  FeedbackAssignRequest,
  FeedbackAttachmentDto,
  FeedbackAttachmentUploadEvent,
  FeedbackCreateRequest,
  FeedbackListResponse,
  FeedbackPatchRequest,
  FeedbackPriorityRequest,
  FeedbackRecordDto,
  FeedbackReplyDto,
  FeedbackReplyRequest,
  FeedbackUpdateRequest,
} from '../types/feedback-api.types';

@Injectable({
  providedIn: 'root',
})
export class FeedbackApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/feedbacks`;

  listAll<T extends FeedbackRecordDto>(
    params: Record<string, string | number | boolean | null | undefined> = {},
  ): Observable<FeedbackListResponse<T> | readonly T[]> {
    return this.requestList<T>(this.baseUrl, params);
  }

  listMine<T extends FeedbackRecordDto>(
    params: Record<string, string | number | boolean | null | undefined> = {},
  ): Observable<FeedbackListResponse<T> | readonly T[]> {
    return this.requestList<T>(this.baseUrl, { ...params, scope: 'MINE' });
  }

  listAssigned<T extends FeedbackRecordDto>(
    params: Record<string, string | number | boolean | null | undefined> = {},
  ): Observable<FeedbackListResponse<T> | readonly T[]> {
    return this.requestList<T>(this.baseUrl, { ...params, scope: 'ASSIGNED' });
  }

  getById(id: number): Observable<Feedback> {
    return this.http.get<Feedback>(`${this.baseUrl}/${id}`);
  }

  create(payload: FeedbackCreateRequest): Observable<Feedback> {
    return this.http.post<Feedback>(this.baseUrl, payload);
  }

  update(id: number, payload: FeedbackUpdateRequest): Observable<Feedback> {
    return this.http.put<Feedback>(`${this.baseUrl}/${id}`, payload);
  }

  patch(id: number, payload: FeedbackPatchRequest): Observable<Feedback> {
    return this.http.patch<Feedback>(`${this.baseUrl}/${id}`, payload);
  }

  updateStatus(id: number, status: FeedbackUpdateRequest['status']): Observable<Feedback> {
    return this.patch(id, { status });
  }

  updatePriority(id: number, payload: FeedbackPriorityRequest): Observable<Feedback> {
    return this.patch(id, { priority: payload.priority });
  }

  assign(id: number, payload: FeedbackAssignRequest): Observable<Feedback> {
    return this.patch(id, payload);
  }

  archive(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  getReplies(id: number): Observable<FeedbackListResponse<FeedbackReplyDto> | readonly FeedbackReplyDto[]> {
    return this.http.get<FeedbackListResponse<FeedbackReplyDto> | readonly FeedbackReplyDto[]>(
      `${this.baseUrl}/${id}/replies`,
    );
  }

  addReply(id: number, payload: FeedbackReplyRequest): Observable<FeedbackReplyDto> {
    return this.http.post<FeedbackReplyDto>(`${this.baseUrl}/${id}/replies`, payload);
  }

  markReplyRead(id: number, rid: number): Observable<FeedbackReplyDto> {
    return this.http.patch<FeedbackReplyDto>(`${this.baseUrl}/${id}/replies/${rid}/read`, {});
  }

  deleteReply(id: number, rid: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}/replies/${rid}`);
  }

  getAttachments(id: number): Observable<readonly FeedbackAttachmentDto[]> {
    return this.http.get<readonly FeedbackAttachmentDto[]>(`${this.baseUrl}/${id}/attachments`);
  }

  getAssignableAssignees(): Observable<FeedbackAssigneeOptionsResponse> {
    return this.http.get<FeedbackAssigneeOptionsResponse>(`${this.baseUrl}/assignees`);
  }

  addAttachments(id: number, files: readonly File[]): Observable<readonly FeedbackAttachmentDto[]> {
    return this.addAttachmentsWithProgress(id, files).pipe(
      filter((event) => event.kind === 'completed'),
      map((event) => event.attachments ?? []),
    );
  }

  addAttachmentsWithProgress(id: number, files: readonly File[]): Observable<FeedbackAttachmentUploadEvent> {
    const formData = new FormData();

    for (const file of files) {
      formData.append('files', file);
    }

    return this.http
      .post<readonly FeedbackAttachmentDto[]>(`${this.baseUrl}/${id}/attachments`, formData, {
        observe: 'events',
        reportProgress: true,
      })
      .pipe(
        map((event): FeedbackAttachmentUploadEvent | null => {
          switch (event.type) {
            case HttpEventType.Sent:
              return { kind: 'progress', progress: 0 };
            case HttpEventType.UploadProgress: {
              const total = event.total ?? 0;
              const progress = total > 0 ? Math.round((event.loaded / total) * 100) : 0;
              return { kind: 'progress', progress };
            }
            case HttpEventType.Response:
              return {
                kind: 'completed',
                progress: 100,
                attachments: event.body ?? [],
              };
            default:
              return null;
          }
        }),
        filter((event): event is FeedbackAttachmentUploadEvent => event !== null),
      );
  }

  downloadAttachment(documentId: number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/attachments/${documentId}/download`, { responseType: 'blob' });
  }

  private requestList<T extends FeedbackRecordDto>(
    url: string,
    params: Record<string, string | number | boolean | null | undefined>,
  ): Observable<FeedbackListResponse<T> | readonly T[]> {
    let httpParams = new HttpParams();

    for (const [key, value] of Object.entries(params)) {
      if (value === null || value === undefined || value === '') {
        continue;
      }

      httpParams = httpParams.set(key, String(value));
    }

    const requestUrl = httpParams.toString() ? `${url}?${httpParams.toString()}` : url;

    return this.http.get<FeedbackListResponse<T> | readonly T[]>(requestUrl);
  }
}
